import React, { useState, useMemo, useCallback } from 'react';
import { array, func } from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { I18n } from 'react-redux-i18n';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from '@ag-grid-community/react';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { dateValueFormatter, dateWithoutTimeComparator, currentLocaleText } from '../../lib/ag-grid';
import BulkActionsSidebar from '../BulkActionsSidebar';
import ConfirmationModal from '../reusable/ConfirmationModal';
import reportsBulkActions from '../../table/bulkActions/reports';
import useBulkSidebarState from '../../hooks/useBulkSidebarState';
import downloadZippedArchiveFromReports from '../Reports/reports.utils';
import * as actions from '../../table/actionsList';
import { reportTypesMap } from '../../store/reports/reducer';
import { ReactComponent as CompletedIcon } from '../../assets/icons/check.svg';
import { ReactComponent as UnreadIcon } from '../../assets/icons/unread.svg';
import { ReactComponent as ProgressIcon } from '../../assets/icons/progress.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning.svg';
import { findReportFormat } from '../../table/bulkActions/reports';
import { formatIconsMap } from '../../table/columns/reports';
import { capitalize } from 'lodash';

ModuleRegistry.registerModules([ClientSideRowModelModule, StatusBarModule, SetFilterModule]);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-068280}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Psytech_International_Ltd}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{GeneSys}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{GeneSys}_need_to_be_licensed___{GeneSys}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{4_October_2025}____[v3]_[01]_MTc1OTUzMjQwMDAwMA==7bc27880cfb7d8f1c736be6bd6fb9894',
);

export const getReportID = (report) => (report && report.status === 'pending' ? 'requestID' : 'notificationID');

const statusIconsMap = {
  viewed: CompletedIcon,
  unread: UnreadIcon,
  pending: ProgressIcon,
  error: WarningIcon,
};

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
   100% {
     transform: rotate(360deg);
   }
`;

const animation = () =>
  css`
    ${rotate} 2s infinite linear;
  `;

const styleIcon = (Icon, status, withMargin) => styled(Icon)`
  margin-right: ${() => withMargin && '10px'};
  width: 18px;
  height: 18px;
  animation: ${() => (status === 'pending' ? animation : 'none')};

  .a {
    ${() =>
      status === 'error' &&
      css`
        fill: ${(props) => props.theme.colors.red};
        stroke: ${(props) => props.theme.colors.red};
        stroke-width: 0.3px;
      `}
  }
`;

const StatusCell = ({ reportType, error }) => {
  const resolvedStatus = reportTypesMap[reportType];
  const finalizedStatus = error ? 'error' : resolvedStatus;
  const Icon = resolvedStatus !== 'deleted' && styleIcon(statusIconsMap[finalizedStatus], finalizedStatus, true);
  return (
    <StatusContainer>
      {resolvedStatus !== 'deleted' && <Icon />}
      <StatusLabel error={error}>
        {finalizedStatus === 'error' ? I18n.t('Support ticket raised') : I18n.t(capitalize(finalizedStatus))}
      </StatusLabel>
    </StatusContainer>
  );
};

const RespondentReports = ({ reports, deleteReports, markReportsAsViewed, fetchRespondent }) => {
  const [selectedReports, setSelectedReports] = useState([]);
  const [isDeletionModalVisible, setDeletionModalStatus] = useState(false);
  const openConfirmationModal = () => setDeletionModalStatus(true);
  const closeConfirmationModal = () => {
    setDeletionModalStatus(false);
  };

  const onDeleteConfirm = () => {
    const data = selectedReports.map((report) => ({
      reportType: report.reportType,
      notificationID: report.notificationID || null,
      requestID: report.requestID || null,
    }));

    deleteReports(data, () => {
      closeConfirmationModal();
      fetchRespondent();
    });
  };

  const onDownloadClick = (reports) => {
    const items = selectedReports.length ? selectedReports : reports;
    if (items.length > 1)
      downloadZippedArchiveFromReports(
        items.filter((item) => item.reportType !== 1),
        () => markReportsAsViewed(items.map((item) => item.notificationID)),
      );
    else
      items.forEach((report) => {
        const link = document.createElement('a');
        link.href = report.downloadLink;
        link.download = report.downloadLink.substr(report.downloadLink.lastIndexOf('/') + 1);
        link.click();
      });
    markReportsAsViewed(items.map((item) => item.notificationID));
  };

  const openMultipleReports = (reports) => {
    const items = selectedReports.length ? selectedReports : reports;
    if (!items.length) return;
    items.forEach((report) => {
      window.open(report.downloadLink, '_blank');
    });
    markReportsAsViewed(items.map((item) => item.notificationID));
  };

  const { isSidebarOpen, closeSidebar } = useBulkSidebarState({ items: selectedReports });

  // BULK ACTIONS
  const bulkActionsHandlers = {
    [actions.OPEN_REPORT]: openMultipleReports,
    [actions.DOWNLOAD_REPORT]: onDownloadClick,
    [actions.DELETE_REPORT]: openConfirmationModal,
  };
  const bulkActions = useMemo(() => {
    return reportsBulkActions.map((item) => ({
      ...item,
      handler: bulkActionsHandlers[item.name],
      isDisabled: typeof item.isDisabled === 'function' ? item.isDisabled(selectedReports) : item.isDisabled,
    }));
  }, [selectedReports]);

  const agGridDefaultSettings = {
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: [20, 50, 100],
    rowHeight: '58',
    headerHeight: '58',
    domLayout: 'autoHeight',
    gridOptions: { popupParent: document.body },
    onSelectionChanged: (event) => {
      const selectedData = [];
      event.api.forEachNodeAfterFilter((node) => {
        if (node.isSelected()) {
          selectedData.push(node.data);
        }
      });
      setSelectedReports(selectedData);
    },
  };

  const agGriddefaultColumnDefinition = {
    sortable: true,
  };

  const [columnDefinition] = useState([
    {
      headerName: I18n.t('Report'),
      celldDtaType: 'text',
      filter: 'agTextColumnfilter',
      flex: 1,
      valueGetter: ({ data }) => data.name || data.reportName || data.questionnaire,
      cellRenderer: ({ data }) => {
        const resolvedName = data.name || data.reportName || data.questionnaire;
        const Icon = formatIconsMap[findReportFormat(data)];
        return (
          <Row>
            {data.downloadLink && <Icon />}
            <span>{resolvedName}</span>
          </Row>
        );
      },
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'reportType',
      headerName: I18n.t('Status'),
      celldDtatTpe: 'text',
      filter: 'agSetColumnfilter',
      flex: 1,
      valueGetter: ({ data }) => reportTypesMap[data.reportType],
      cellRenderer: ({ data }) => {
        return <StatusCell reportType={data.reportType} error={data.error} />;
      },
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'creationDate',
      headerName: I18n.t('Creation Date'),
      cellDataType: 'date',
      flex: 1,
      filter: 'agDateColumnFilter',
      valueGetter: (params) => {
        return new Date(params.data.creationDate);
      },
      valueFormatter: dateValueFormatter,
      filterParams: {
        maxNumConditions: 1,
        comparator: dateWithoutTimeComparator,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
  ]);

  const rowSelection = useMemo(() => {
    return {
      mode: 'multiRow',
    };
  }, []);

  function getRowClass(params) {
    if (params.data.reportType === 2) {
      return 'row-unread';
    }
    return null;
  }

  const handleFilterChanged = (params) => {
    params.api.deselectAll();
    params.api.refreshHeader();
  };

  const onGridReady = useCallback((params) => {
    params.api.applyColumnState({
      defaultState: { sort: null },
      state: [{ colId: 'creationDate', sort: 'desc' }],
    });
  }, []);

  return (
    <RespondentReportsContainer>
      <BulkActionsSidebar actions={bulkActions} onClose={closeSidebar} isOpen={isSidebarOpen} />
      <div className="ag-theme-quartz ag-grid-custom-styles">
        <AgGridReact
          {...agGridDefaultSettings}
          defaultColDef={agGriddefaultColumnDefinition}
          columnDefs={columnDefinition}
          rowData={reports}
          rowSelection={rowSelection}
          onFilterChanged={handleFilterChanged}
          getRowClass={getRowClass}
          localeText={currentLocaleText}
          onGridReady={onGridReady}
        />
      </div>
      <ConfirmationModal
        title={I18n.t('Report deletion')}
        onClose={closeConfirmationModal}
        isVisible={isDeletionModalVisible}
        description={I18n.t('Are you sure you want to delete selected report?')}
        onConfirm={onDeleteConfirm}
        caution
      />
    </RespondentReportsContainer>
  );
};

RespondentReports.propTypes = {
  reports: array.isRequired,
  deleteReports: func.isRequired,
  markReportsAsViewed: func.isRequired,
  fetchRespondent: func.isRequired,
};

const RespondentReportsContainer = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  position: relative;
  height: calc(100% - 30px);
  overflow-y: auto;
  padding-bottom: 10px;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.error ? props.theme.colors.red : props.theme.colors.primary)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

export default RespondentReports;
